import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import VideoPlayer from '../components/VideoPlayer';
import CommentPanel from '../components/CommentPanel';

const API_BASE = process.env.REACT_APP_API_URL || '';
const SESSION_KEY = 'jv_guest_session';

export default function ShareLinkViewer() {
  const { token } = useParams();
  const seekRef   = useRef(null);

  const [data,               setData]               = useState(null);
  const [loading,            setLoading]            = useState(true);
  const [inactive,           setInactive]           = useState(false);
  const [comments,           setComments]           = useState([]);
  const [currentTimeSeconds, setCurrentTimeSeconds] = useState(0);
  const [showGuestModal,     setShowGuestModal]     = useState(false);

  const [guestSession, setGuestSession] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(SESSION_KEY)); }
    catch { return null; }
  });

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/share/${token}`);
      setData(res.data);
      setComments(res.data.comments || []);
    } catch (err) {
      if (err.response?.status === 404) setInactive(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function refreshComments() {
    try {
      const res = await axios.get(`${API_BASE}/api/share/${token}`);
      setComments(res.data.comments || []);
    } catch {}
  }

  async function handleGuestCommentPost({ timestampSeconds, text }) {
    await axios.post(
      `${API_BASE}/api/share/${token}/comment`,
      { timestampSeconds, text },
      { headers: { Authorization: `Bearer ${guestSession.guestToken}` } },
    );
  }

  function handleDownload() {
    axios.get(`${API_BASE}/api/share/${token}/download`)
      .then(res => window.open(res.data.url, '_blank'))
      .catch(() => alert('Download failed. Please try again.'));
  }

  if (loading) {
    return (
      <div style={FULL_CENTER}>
        <span style={{ fontSize: 13, color: 'var(--tx-faint)' }}>Loading…</span>
      </div>
    );
  }

  if (inactive || !data) {
    return (
      <div style={FULL_CENTER}>
        <div style={{ textAlign: 'center', padding: '0 32px' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 10 }}>
            This link is no longer active.
          </div>
          <div style={{ fontSize: 13, color: 'var(--tx-faint)', lineHeight: 1.6 }}>
            The share link you followed has been deactivated or has expired.
          </div>
        </div>
      </div>
    );
  }

  const { shareLink, videoAsset, clientName } = data;

  return (
    <div className="slv-root" style={{ background: '#07070c', minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font)', color: 'var(--tx-primary)' }}>
      <style>{SLV_RESPONSIVE_CSS}</style>

      {/* Top bar */}
      <div style={{
        height: 44, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(7,7,12,0.92)',
        backdropFilter: 'blur(8px)',
      }}>
        <div style={{ fontSize: 12, color: 'var(--tx-secondary)', display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
          {clientName && <span style={{ fontWeight: 600, flexShrink: 0 }}>{clientName}</span>}
          {clientName && <span style={{ opacity: 0.35, flexShrink: 0 }}>·</span>}
          <span style={{ color: 'var(--tx-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{videoAsset.fileName}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {shareLink.allowDownload && (
            <button className="btn-ghost" style={{ fontSize: 12 }} onClick={handleDownload}>
              ↓ Download
            </button>
          )}
          <span style={{ fontSize: 11, color: 'var(--tx-faint)', fontWeight: 600, opacity: 0.5 }}>Heliconflow</span>
        </div>
      </div>

      {/* Two-column body — stacks vertically below 768px (see SLV_RESPONSIVE_CSS). */}
      <div className="slv-body" style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>

        {/* Video column */}
        <div className="slv-video" style={{ flex: '0 0 75%', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: '#07070c', padding: 24 }}>
          <VideoPlayer
            src={videoAsset.playbackUrl || videoAsset.cloudfrontUrl}
            comments={comments}
            seekRef={seekRef}
            onTimeUpdate={setCurrentTimeSeconds}
          />
        </div>

        {/* Comment panel */}
        <div className="slv-side" style={{ flex: '0 0 25%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <CommentPanel
            videoAssetId={videoAsset.id}
            currentTimeSeconds={currentTimeSeconds}
            onSeekTo={secs => seekRef.current?.(secs)}
            comments={comments}
            onCommentsChange={refreshComments}
            isExternal
            guestSession={guestSession}
            allowComments={shareLink.allowComments}
            onGuestLoginRequired={() => setShowGuestModal(true)}
            onCommentPost={handleGuestCommentPost}
          />
        </div>
      </div>

      {/* Guest login modal */}
      {showGuestModal && (
        <GuestLoginModal
          token={token}
          onSuccess={session => {
            setGuestSession(session);
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
            setShowGuestModal(false);
          }}
          onClose={() => setShowGuestModal(false)}
        />
      )}
    </div>
  );
}

function GuestLoginModal({ token, onSuccess, onClose }) {
  const [name,       setName]       = useState('');
  const [email,      setEmail]      = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim())  { setError('Name is required.'); return; }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.'); return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE}/api/share/${token}/guest-session`, {
        name: name.trim(), email: email.trim(),
      });
      onSuccess({ name: name.trim(), email: email.trim(), guestToken: res.data.guestToken });
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 500 }}>
      <div
        style={{
          background: 'var(--bg-card)', borderRadius: 12, padding: '28px 32px',
          width: 360, border: '1px solid var(--border-mid)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.6)', fontFamily: 'var(--font)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 6 }}>
          Enter your details to comment
        </div>
        <div style={{ fontSize: 12, color: 'var(--tx-faint)', marginBottom: 20, lineHeight: 1.5 }}>
          Your name will appear next to your comments.
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            className="vc-input"
            placeholder="Your name"
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
          />
          <input
            className="vc-input"
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          {error && (
            <div style={{ fontSize: 11, color: 'var(--s-incomplete-t)' }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const FULL_CENTER = {
  background: '#07070c',
  minHeight:  '100vh',
  display:    'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--font)',
};

// Responsive overrides for narrow viewports (phones). Below 768px the two
// columns stack — video on top in a 16:9 box, comments fill the rest. Uses
// `100dvh` so the iOS keyboard doesn't push the comment input off-screen.
const SLV_RESPONSIVE_CSS = `
  @media (max-width: 768px) {
    .slv-root { min-height: 100dvh !important; }
    .slv-body { flex-direction: column !important; }
    .slv-video {
      flex: 0 0 auto !important;
      width: 100% !important;
      height: 56.25vw !important;
      max-height: 45vh !important;
      min-height: 180px !important;
      padding: 8px !important;
      justify-content: flex-start !important;
    }
    .slv-side {
      flex: 1 1 0 !important;
      min-width: 0 !important;
      min-height: 55vh !important;
    }
  }
`;
